import type { ApiResponse } from "@/features/auth/services/authApi";
import { apiClient } from "@/shared/services/api";

// Mirrors backend PaymentMethodAccountType. Legacy API responses serialize enums as
// integers, while Phase 2 currently serializes them as enum names.
export const PAYMENT_METHOD_TYPE = {
  Cash: 1,
  MpesaTill: 2,
  MpesaPaybill: 3,
  Bank: 4,
} as const;
export type PaymentMethodType =
  (typeof PAYMENT_METHOD_TYPE)[keyof typeof PAYMENT_METHOD_TYPE];

export const PAYMENT_METHOD_APPROVAL_STATUS = {
  Pending: 1,
  Approved: 2,
  Rejected: 3,
} as const;
export type PaymentMethodApprovalStatus =
  (typeof PAYMENT_METHOD_APPROVAL_STATUS)[keyof typeof PAYMENT_METHOD_APPROVAL_STATUS];

export interface PaymentMethodAccount {
  id: string;
  name: string;
  code: string;
  type: PaymentMethodType;
  typeName: string;
  isActive: boolean;
  tillNumber?: string | null;
  paybillNumber?: string | null;
  mpesaAccountNumber?: string | null;
  provider?: string | null;
  bankAccountId?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  bankName?: string | null;
  glAccountId?: string | null;
  approvalStatus: PaymentMethodApprovalStatus;
  approvalStatusName: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  approvedByUserId?: string | null;
  approvedByUserName?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
}

type PaymentMethodAccountWire = Omit<
  PaymentMethodAccount,
  "type" | "approvalStatus"
> & {
  type: PaymentMethodType | string;
  approvalStatus: PaymentMethodApprovalStatus | string;
};

export interface CreatePaymentMethodRequest {
  name: string;
  code: string;
  type: PaymentMethodType;
  tillNumber?: string;
  paybillNumber?: string;
  mpesaAccountNumber?: string;
  provider?: string;
  bankAccountId?: string;
  glAccountId?: string;
}

export interface UpdatePaymentMethodRequest {
  name: string;
  tillNumber?: string;
  paybillNumber?: string;
  mpesaAccountNumber?: string;
  provider?: string;
  bankAccountId?: string;
  glAccountId?: string;
}

export interface ApprovePaymentMethodRequest {
  approvalNotes?: string;
}

export interface RejectPaymentMethodRequest {
  rejectionReason: string;
}

const unwrap = <T>(data: ApiResponse<T> | T): T => {
  if (data && typeof data === "object" && "data" in (data as ApiResponse<T>)) {
    return (data as ApiResponse<T>).data;
  }
  return data as T;
};

const normalizeEnumValue = <T extends number>(
  value: T | string,
  values: Record<string, T>,
  fieldName: string
): T => {
  if (typeof value === "number") return value;

  const namedValue = values[value];
  if (namedValue !== undefined) return namedValue;

  const numericValue = Number(value);
  if (Number.isInteger(numericValue) && Object.values(values).includes(numericValue as T)) {
    return numericValue as T;
  }

  throw new Error(`Unsupported payment method ${fieldName}: ${value}`);
};

export const normalizePaymentMethodAccount = (
  account: PaymentMethodAccountWire
): PaymentMethodAccount => ({
  ...account,
  type: normalizeEnumValue(account.type, PAYMENT_METHOD_TYPE, "type"),
  approvalStatus: normalizeEnumValue(
    account.approvalStatus,
    PAYMENT_METHOD_APPROVAL_STATUS,
    "approval status"
  ),
});

const unwrapAccount = (
  data: ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
): PaymentMethodAccount => normalizePaymentMethodAccount(unwrap(data));

const unwrapAccounts = (
  data: ApiResponse<PaymentMethodAccountWire[]> | PaymentMethodAccountWire[]
): PaymentMethodAccount[] => unwrap(data).map(normalizePaymentMethodAccount);

export const paymentMethodsApi = {
  /** Approved + active list — used to populate the payment-recording dropdown. */
  list: async (): Promise<PaymentMethodAccount[]> => {
    const { data } = await apiClient.get<
      ApiResponse<PaymentMethodAccountWire[]> | PaymentMethodAccountWire[]
    >("/payment-methods");
    return unwrapAccounts(data);
  },

  /** GM approval queue. */
  listPending: async (): Promise<PaymentMethodAccount[]> => {
    const { data } = await apiClient.get<
      ApiResponse<PaymentMethodAccountWire[]> | PaymentMethodAccountWire[]
    >("/payment-methods/pending");
    return unwrapAccounts(data);
  },

  /** Submissions made by the current user (any status). */
  mySubmissions: async (): Promise<PaymentMethodAccount[]> => {
    const { data } = await apiClient.get<
      ApiResponse<PaymentMethodAccountWire[]> | PaymentMethodAccountWire[]
    >("/payment-methods/my-submissions");
    return unwrapAccounts(data);
  },

  getById: async (id: string): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.get<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >(`/payment-methods/${id}`);
    return unwrapAccount(data);
  },

  provision: async (
    payload: CreatePaymentMethodRequest
  ): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.post<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >("/payment-methods", payload);
    return unwrapAccount(data);
  },

  update: async (
    id: string,
    payload: UpdatePaymentMethodRequest
  ): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.put<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >(`/payment-methods/${id}`, payload);
    return unwrapAccount(data);
  },

  approve: async (
    id: string,
    payload: ApprovePaymentMethodRequest = {}
  ): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.post<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >(`/payment-methods/${id}/approve`, payload);
    return unwrapAccount(data);
  },

  reject: async (
    id: string,
    payload: RejectPaymentMethodRequest
  ): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.post<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >(`/payment-methods/${id}/reject`, payload);
    return unwrapAccount(data);
  },

  deactivate: async (id: string): Promise<PaymentMethodAccount> => {
    const { data } = await apiClient.post<
      ApiResponse<PaymentMethodAccountWire> | PaymentMethodAccountWire
    >(`/payment-methods/${id}/deactivate`, {});
    return unwrapAccount(data);
  },
};

export const isMpesaType = (type: PaymentMethodType): boolean =>
  type === PAYMENT_METHOD_TYPE.MpesaTill ||
  type === PAYMENT_METHOD_TYPE.MpesaPaybill;

export const formatPaymentMethodLabel = (
  account: PaymentMethodAccount
): string => {
  switch (account.type) {
    case PAYMENT_METHOD_TYPE.MpesaTill:
      return `${account.name} (Till ${account.tillNumber ?? ""})`;
    case PAYMENT_METHOD_TYPE.MpesaPaybill:
      return `${account.name} (Paybill ${account.paybillNumber ?? ""})`;
    case PAYMENT_METHOD_TYPE.Bank:
      return `${account.name} (${account.bankName ?? "Bank"})`;
    default:
      return account.name;
  }
};
